import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import type { User, UserFormData } from '../types';

export const getUserByFirebaseUid = async (uid: string): Promise<User | null> => {
  const q = query(collection(db, 'users'), where('firebaseUid', '==', uid), where('status', '==', 'active'));
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) return null;
  const d = querySnapshot.docs[0];
  return { id: d.id, ...d.data() } as User;
};

export const getUsers = async (): Promise<User[]> => {
  const q = query(collection(db, 'users'), where('status', 'in', ['active', 'inactive']));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as User));
};

export const getUsersByUniversity = async (universityId: string): Promise<User[]> => {
  const q = query(collection(db, 'users'), where('universityId', '==', universityId), where('status', 'in', ['active', 'inactive']));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as User));
};

export const checkEmailExists = async (email: string): Promise<boolean> => {
  const q = query(collection(db, 'users'), where('email', '==', email), where('status', 'in', ['active', 'inactive']));
  const querySnapshot = await getDocs(q);
  return !querySnapshot.empty;
};

const findArchivedUserByEmail = async (email: string): Promise<User | null> => {
  const q = query(collection(db, 'users'), where('email', '==', email), where('status', '==', 'archived'));
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) return null;
  const d = querySnapshot.docs[0];
  return { id: d.id, ...d.data() } as User;
};

export const addUser = async (userData: UserFormData): Promise<User> => {
  const { password, ...dataWithoutPassword } = userData;

  // Check if an archived user with this email exists — reactivate instead of creating
  const archivedUser = await findArchivedUserByEmail(userData.email);
  if (archivedUser) {
    const reactivatedData = { ...dataWithoutPassword, status: userData.status || 'active' };
    const userRef = doc(db, 'users', archivedUser.id);
    await updateDoc(userRef, reactivatedData);
    return { ...archivedUser, ...reactivatedData } as User;
  }

  // New user with password (admin) — create Firebase Auth account
  if (userData.password) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, userData.email, userData.password);
      const dataWithUid = { ...dataWithoutPassword, firebaseUid: userCredential.user.uid };
      const docRef = await addDoc(collection(db, 'users'), dataWithUid);
      return { id: docRef.id, ...dataWithUid } as User;
    } catch (authError: any) {
      if (authError.code === 'auth/email-already-in-use') {
        throw new Error(`Email address "${userData.email}" is already registered. Please use a different email address.`);
      } else if (authError.code === 'auth/invalid-email') {
        throw new Error('Invalid email address format. Please enter a valid email address.');
      } else if (authError.code === 'auth/weak-password') {
        throw new Error('Password is too weak. Please use a stronger password (at least 6 characters).');
      }
      throw new Error(`Failed to create user account: ${authError.message}`);
    }
  }

  // New user without password (regular user)
  const docRef = await addDoc(collection(db, 'users'), dataWithoutPassword);
  return { id: docRef.id, ...dataWithoutPassword } as User;
};

export const updateUser = async (id: string, userData: Partial<UserFormData>): Promise<User> => {
  const { password, ...dataWithoutPassword } = userData;
  const userRef = doc(db, 'users', id);
  await updateDoc(userRef, dataWithoutPassword);
  return { id, ...dataWithoutPassword } as User;
};

export const deleteUser = async (id: string): Promise<string> => {
  const userRef = doc(db, 'users', id);
  await updateDoc(userRef, { status: 'archived' });
  return id;
};
